import React, { useState, useEffect, useRef } from 'react';
import BBALoader from '../components/BBALoader';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import AddButton from '../components/AddButton';
import EditButton from '../components/EditButton';
import { useNavigate } from 'react-router-dom';
import FinancePageHeader from '../components/finance/FinancePageHeader';
import { useFinanceEmbedded } from '../hooks/useFinanceEmbedded';
import { FaArrowLeft } from 'react-icons/fa';
import { loadOverheadData, saveOverheadData } from '../services/overheadService';

// overrideUserId lets an internal viewer (CRM Portal View) load/save a specific
// client's overhead data instead of their own.
const OverheadCalculator = ({ embedded: embeddedProp, overrideUserId = null }) => {
  const embedded = useFinanceEmbedded(embeddedProp);
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  // The uid whose overhead data is read/written.
  const effectiveUid = overrideUserId || currentUser?.uid;

  // Guards so simply viewing a client (override) doesn't overwrite their data:
  // autosave only fires after the initial load, and the redundant save caused
  // by loading data into state is skipped.
  const didLoadRef = useRef(false);
  const skipNextSaveRef = useRef(false);
  
  // Loading state (saving happens automatically in the background)
  const [loading, setLoading] = useState(true);

  // State for time period selection
  const [timePeriod, setTimePeriod] = useState('Annual');

  // State for table view mode for each category
  const [viewMode, setViewMode] = useState({});

  // Input mode: 'detailed' = itemized tables; 'quick' = one fixed total.
  const [inputMode, setInputMode] = useState('detailed');
  const [quickAmount, setQuickAmount] = useState('');
  const [quickFreq, setQuickFreq] = useState('Monthly');

  // State for each category of expenses with placeholder examples
  const [expenses, setExpenses] = useState({
    'Operational Overhead': [
      { description: 'Rent', amount: 0, frequency: 'Monthly' },
      { description: 'Utilities', amount: 0, frequency: 'Monthly' },
      { description: 'Office Supplies', amount: 0, frequency: 'Quarterly' },
      { description: 'Marketing & Advertising', amount: 0, frequency: 'Monthly' },
      { description: 'Professional Services', amount: 0, frequency: 'Annual' },
      { description: 'Office Maintenance & Cleaning', amount: 0, frequency: 'Monthly' }
    ],
    'Operation Payroll': [
      { description: 'Salaries & Wages', amount: 0, frequency: 'Annual' },
      { description: 'Payroll Taxes & Benefits', amount: 0, frequency: 'Annual' },
      { description: 'Workers\' Compensation', amount: 0, frequency: 'Annual' },
      { description: 'Bonuses & Incentives', amount: 0, frequency: 'Quarterly' }
    ],
    'Asset Payments': [
      { description: 'Ford F150', amount: 0, frequency: 'Monthly' },
      { description: 'Skid Steer', amount: 0, frequency: 'Monthly' }
    ],
    'Subscriptions': [
      { description: 'Tech Stack', amount: 0, frequency: 'Annual' },
      { description: 'Industry Memberships', amount: 0, frequency: 'Annual' },
      { description: 'Training & Education', amount: 0, frequency: 'Monthly' }
    ],
    'Insurance': [
      { description: 'General Liability Insurance', amount: 0, frequency: 'Annual' },
      { description: 'Workers\' Comp Insurance', amount: 0, frequency: 'Annual' },
      { description: 'Health Insurance Contributions', amount: 0, frequency: 'Monthly' },
      { description: 'Vehicle Insurance', amount: 0, frequency: 'Annual' }
    ],
    'Debt Service': [
      { description: 'Business Loan Repayments', amount: 0, frequency: 'Monthly' },
      { description: 'Credit Card Payments', amount: 0, frequency: 'Monthly' }
    ],
    'Soft Debt': [
      { description: 'Internal Loans', amount: 0, frequency: 'Monthly' }
    ]
  });
  
  // State for summary values
  const [summary, setSummary] = useState({
    fixedOverhead: 0,
    softOverhead: 0,
    totalOverhead: 0
  });
  
  // Load overhead data on component mount
  useEffect(() => {
    const loadData = async () => {
      if (!effectiveUid) return;

      try {
        setLoading(true);
        const data = await loadOverheadData(effectiveUid);
        setExpenses(data.expenses || expenses);
        setTimePeriod(data.timePeriod || 'Annual');
        setInputMode(data.inputMode || 'detailed');
        setQuickAmount(data.quickOverhead?.amount ? String(data.quickOverhead.amount) : '');
        setQuickFreq(data.quickOverhead?.frequency || 'Monthly');
        // Skip the autosave triggered by loading data into state.
        skipNextSaveRef.current = true;
        didLoadRef.current = true;
      } catch (error) {
        console.error('Error loading overhead data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [effectiveUid]);

  // Auto-save functionality
  const autoSave = async () => {
    if (!effectiveUid || loading) return;

    try {
      await saveOverheadData({
        expenses,
        timePeriod,
        inputMode,
        quickOverhead: { amount: parseFloat(quickAmount) || 0, frequency: quickFreq }
      }, effectiveUid);
    } catch (error) {
      console.error('Error auto-saving overhead data:', error);
    }
  };

  // Auto-save when expenses change
  useEffect(() => {
    if (!didLoadRef.current) return; // nothing loaded yet — don't save defaults
    if (skipNextSaveRef.current) { skipNextSaveRef.current = false; return; }
    const timeoutId = setTimeout(() => {
      autoSave();
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [expenses, timePeriod, inputMode, quickAmount, quickFreq]);

  // Handle time period change
  const handleTimePeriodChange = (period) => {
    setTimePeriod(period);
  };

  // Annualize an amount at a given period (Weekly/Monthly/Quarterly/Annual).
  const annualizeAt = (amount, period) =>
    period === 'Weekly' ? amount * 52 : period === 'Quarterly' ? amount * 4 : period === 'Monthly' ? amount * 12 : amount;

  // Switching the quick-entry period keeps the same annual total (converts the
  // typed amount), so picking Monthly vs Annual re-expresses the same overhead.
  const handleQuickFreqChange = (newFreq) => {
    const amount = parseFloat(quickAmount) || 0;
    if (amount > 0) {
      const annual = annualizeAt(amount, quickFreq);
      const converted =
        newFreq === 'Weekly' ? annual / 52 : newFreq === 'Quarterly' ? annual / 4 : newFreq === 'Monthly' ? annual / 12 : annual;
      setQuickAmount(String(Math.round(converted)));
    }
    setQuickFreq(newFreq);
  };
  
  // Add a new expense row to a category
  const addExpense = (category) => {
    setExpenses({
      ...expenses,
      [category]: [
        ...expenses[category],
        { description: '', amount: 0, frequency: 'Monthly' }
      ]
    });
  };
  
  // Update an expense
  const updateExpense = (category, index, field, value) => {
    const updatedCategory = [...expenses[category]];
    updatedCategory[index] = {
      ...updatedCategory[index],
      [field]: field === 'amount' ? parseFloat(value) || 0 : value
    };
    
    setExpenses({
      ...expenses,
      [category]: updatedCategory
    });
  };
  
  // Remove an expense
  const removeExpense = (category, index) => {
    const updatedCategory = [...expenses[category]];
    updatedCategory.splice(index, 1);
    
    setExpenses({
      ...expenses,
      [category]: updatedCategory
    });
  };
  
  // Toggle view mode for a category
  const toggleViewMode = (category) => {
    setViewMode(prev => ({
      ...prev,
      [category]: prev[category] === 'percentage' ? 'table' : 'percentage'
    }));
  };
  
  // Calculate the adjusted amount based on frequency and selected time period for summary cards
  const calculateAdjustedAmount = (amount, frequency) => {
    // First convert to annual amount
    let annualAmount = 0;
    if (frequency === 'Weekly') {
      annualAmount = amount * 52;
    } else if (frequency === 'Monthly') {
      annualAmount = amount * 12;
    } else if (frequency === 'Quarterly') {
      annualAmount = amount * 4;
    } else if (frequency === 'Annual') {
      annualAmount = amount;
    }
    
    // Then convert from annual to selected time period
    if (timePeriod === 'Weekly') {
      return annualAmount / 52;
    } else if (timePeriod === 'Monthly') {
      return annualAmount / 12;
    } else if (timePeriod === 'Quarterly') {
      return annualAmount / 4;
    } else {
      return annualAmount;
    }
  };
  
  // Calculate annual amount for expense tables
  const calculateAnnualAmount = (amount, frequency) => {
    if (frequency === 'Weekly') {
      return amount * 52;
    } else if (frequency === 'Monthly') {
      return amount * 12;
    } else if (frequency === 'Quarterly') {
      return amount * 4;
    } else {
      return amount;
    }
  };
  
  // Calculate all summary values
  useEffect(() => {
    // Quick mode: one fixed total entered at the selected period — that amount
    // IS the total at that period (all counted as fixed overhead).
    if (inputMode === 'quick') {
      const amount = parseFloat(quickAmount) || 0;
      setSummary({ fixedOverhead: amount, softOverhead: 0, totalOverhead: amount });
      return;
    }

    let fixedTotal = 0;
    let softTotal = 0;

    // Calculate fixed and soft overhead
    Object.entries(expenses).forEach(([category, categoryExpenses]) => {
      categoryExpenses.forEach(expense => {
        const adjustedAmount = calculateAdjustedAmount(expense.amount, expense.frequency);

        // Soft overhead: Debt Service and Soft Debt categories
        if (category === 'Soft Debt' || category === 'Debt Service') {
          softTotal += adjustedAmount;
        } else {
          // Fixed overhead: all other categories
          fixedTotal += adjustedAmount;
        }
      });
    });

    setSummary({
      fixedOverhead: fixedTotal,
      softOverhead: softTotal,
      totalOverhead: fixedTotal + softTotal
    });
  }, [expenses, timePeriod, inputMode, quickAmount, quickFreq]);

  // Annual equivalent of the quick-entry amount, for the "annually" hint.
  const quickAnnual = annualizeAt(parseFloat(quickAmount) || 0, quickFreq);

  return (
    <div className={`${embedded ? '' : 'container mx-auto px-4 py-8 min-h-screen'} ${darkMode ? 'text-white' : 'text-gray-900'}`}>
      {!embedded && <FinancePageHeader title="Overhead Calculator" />}

      {/* Loading State */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <BBALoader size={48} />
        </div>
      ) : (
        <>
          {/* Mode tabs: Quick Entry vs Detail */}
          <div className="flex justify-center mb-6">
            <div className={`border-b-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex`}>
              {[{ id: 'quick', label: 'Quick Entry' }, { id: 'detailed', label: 'Detail' }].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setInputMode(opt.id)}
                  className={`px-8 py-2 font-medium ${inputMode === opt.id ? 'text-red-500 border-b-2 border-red-500 -mb-0.5' : (darkMode ? 'text-gray-400' : 'text-gray-500')}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Summary Cards */}
          <div className="mb-8">
        {/* Period tabs — display period in detail mode, entry period in quick mode */}
        <div className="flex items-center justify-center mb-4">
          <div className={`border-b-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex`}>
            {['Annual', 'Quarterly', 'Monthly', 'Weekly'].map(period => {
              const active = inputMode === 'quick' ? quickFreq === period : timePeriod === period;
              return (
                <button
                  key={period}
                  className={`px-8 py-2 font-medium ${active ? 'text-red-500 border-b-2 border-red-500 -mb-0.5' : (darkMode ? 'text-gray-400' : 'text-gray-500')}`}
                  onClick={() => (inputMode === 'quick' ? handleQuickFreqChange(period) : handleTimePeriodChange(period))}
                >
                  {period}
                </button>
              );
            })}
          </div>
        </div>

        {/* Quick entry input — sits directly under the period tabs */}
        {inputMode === 'quick' && (
          <div className="max-w-sm mx-auto mb-6">
            <label className={`block text-sm font-medium text-center mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Your total {quickFreq.toLowerCase()} overhead
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">$</span>
              <input
                type="number"
                min="0"
                value={quickAmount}
                onChange={(e) => setQuickAmount(e.target.value)}
                placeholder="0"
                className={`w-full pl-8 pr-3 py-3 rounded-lg border text-lg text-center font-semibold focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  darkMode ? 'bg-gray-800 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>
            <p className={`text-xs text-center mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Counted entirely as fixed overhead. Saved automatically and used across the Finance dashboard.
            </p>
          </div>
        )}

          {inputMode === 'quick' ? (
            <div className="flex justify-center">
              <div className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6 flex flex-col items-center justify-center w-full max-w-xs`}>
                <div className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Total Overhead ({quickFreq})</div>
                <div className="text-3xl font-bold">${summary.totalOverhead.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                <div className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  ${quickAnnual.toLocaleString(undefined, { maximumFractionDigits: 0 })} annually
                </div>
              </div>
            </div>
          ) : (
          <div className="grid grid-cols-3 gap-4">
            <div className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6 flex flex-col items-center justify-center`}>
              <div className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Fixed Overhead</div>
              <div className="text-2xl font-bold">${summary.fixedOverhead.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
            </div>
            <div className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6 flex flex-col items-center justify-center`}>
              <div className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Soft Overhead</div>
              <div className="text-2xl font-bold">${summary.softOverhead.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
            </div>
            <div className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6 flex flex-col items-center justify-center`}>
              <div className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Total Overhead</div>
              <div className="text-2xl font-bold">${summary.totalOverhead.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
            </div>
          </div>
          )}
      </div>

      {inputMode === 'detailed' && (<>
      {/* Fixed Overhead Section Title */}
      <div className="text-center mb-6 mt-10">
        <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-700'} inline-block px-6 py-2 border-b-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>Fixed Overhead</h2>
      </div>

      {/* Fixed Overhead Categories */}
      {['Operational Overhead', 'Operation Payroll', 'Asset Payments', 'Subscriptions', 'Insurance'].map(category => (
        <ExpenseCategory
          key={category}
          title={category}
          expenses={expenses[category]}
          addExpense={() => addExpense(category)}
          updateExpense={(index, field, value) => updateExpense(category, index, field, value)}
          removeExpense={(index) => removeExpense(category, index)}
          calculateAdjustedAmount={calculateAdjustedAmount}
          calculateAnnualAmount={calculateAnnualAmount}
          timePeriod={timePeriod}
          viewMode={viewMode[category] || 'table'}
          toggleViewMode={() => toggleViewMode(category)}
          isFixedOverhead={true}
          summary={summary}
          darkMode={darkMode}
        />
      ))}
      
      {/* Soft Overhead Section Title */}
      <div className="text-center mb-6 mt-10">
        <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-700'} inline-block px-6 py-2 border-b-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>Soft Overhead</h2>
      </div>
      
      {/* Soft Overhead Categories */}
      {['Debt Service', 'Soft Debt'].map(category => (
        <ExpenseCategory
          key={category}
          title={category}
          expenses={expenses[category]}
          addExpense={() => addExpense(category)}
          updateExpense={(index, field, value) => updateExpense(category, index, field, value)}
          removeExpense={(index) => removeExpense(category, index)}
          calculateAdjustedAmount={calculateAdjustedAmount}
          calculateAnnualAmount={calculateAnnualAmount}
          timePeriod={timePeriod}
          viewMode={viewMode[category] || 'table'}
          toggleViewMode={() => toggleViewMode(category)}
          isFixedOverhead={false}
          summary={summary}
          darkMode={darkMode}
        />
      ))}
      </>)}
        </>
      )}
    </div>
  );
};

// Component for each expense category
const ExpenseCategory = ({ 
  title, 
  expenses, 
  addExpense, 
  updateExpense, 
  removeExpense, 
  calculateAdjustedAmount,
  calculateAnnualAmount,
  timePeriod,
  viewMode,
  toggleViewMode,
  isFixedOverhead,
  summary,
  darkMode
}) => {
  const categoryTotal = expenses.reduce((total, expense) => {
    return total + calculateAdjustedAmount(expense.amount, expense.frequency);
  }, 0);
  
  // Calculate percentages
  const calculatePercentages = (expense) => {
    const adjustedAmount = calculateAdjustedAmount(expense.amount, expense.frequency);
    
    // Percentage of category
    const categoryPercentage = categoryTotal === 0 ? 0 : (adjustedAmount / categoryTotal) * 100;
    
    // Percentage of fixed/soft overhead
    const overheadTotal = isFixedOverhead ? summary.fixedOverhead : summary.softOverhead;
    const overheadPercentage = overheadTotal === 0 ? 0 : (adjustedAmount / overheadTotal) * 100;
    
    // Percentage of total overhead
    const totalPercentage = summary.totalOverhead === 0 ? 0 : (adjustedAmount / summary.totalOverhead) * 100;
    
    return {
      category: categoryPercentage,
      overhead: overheadPercentage,
      total: totalPercentage
    };
  };
  
  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h2>
        <div className="flex items-center gap-4">
          <div className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Total: ${categoryTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </div>
          <div className={`flex border rounded-lg overflow-hidden border-gray-200 dark:border-gray-700`}>
            <button 
              onClick={toggleViewMode}
              className={`px-3 py-1 ${viewMode === 'table' ? (darkMode ? 'bg-gray-700' : 'bg-gray-200') : (darkMode ? 'bg-gray-800' : 'bg-white')} ${darkMode ? 'text-white' : 'text-gray-900'}`}
              title="Table View"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
            <button 
              onClick={toggleViewMode}
              className={`px-3 py-1 ${viewMode === 'percentage' ? (darkMode ? 'bg-gray-700' : 'bg-gray-200') : (darkMode ? 'bg-gray-800' : 'bg-white')} ${darkMode ? 'text-white' : 'text-gray-900'}`}
              title="Percentage View"
            >
              %
            </button>
          </div>
          <AddButton onClick={addExpense} />
        </div>
      </div>
      
      {expenses.length > 0 ? (
        <div className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm overflow-hidden`}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              {viewMode === 'table' ? (
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider w-1/2`}>Description</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>Amount</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>Frequency</th>
                  <th className="px-6 py-3"></th>
                </tr>
              ) : (
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider w-1/2`}>Description</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>% of {title}</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>% of {isFixedOverhead ? 'Fixed' : 'Soft'} Overhead</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>% of Total Overhead</th>
                  <th className="px-6 py-3"></th>
                </tr>
              )}
            </thead>
            <tbody className={`${darkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {expenses.map((expense, index) => {
                const percentages = calculatePercentages(expense);
                
                return (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="text"
                        className={`border rounded-lg px-3 py-2 w-full border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white`}
                        value={expense.description}
                        onChange={(e) => updateExpense(index, 'description', e.target.value)}
                        placeholder="Description"
                      />
                    </td>
                    
                    {viewMode === 'table' ? (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} mr-2`}>$</span>
                            <input
                              type="number"
                              className={`border rounded-lg px-3 py-2 w-full border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white`}
                              value={expense.amount || ''}
                              onChange={(e) => updateExpense(index, 'amount', e.target.value)}
                              placeholder="0.00"
                              min="0"
                              step="100"
                            />
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            className={`border rounded-lg px-3 py-2 w-full border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white`}
                            value={expense.frequency}
                            onChange={(e) => updateExpense(index, 'frequency', e.target.value)}
                          >
                            <option value="Weekly">Weekly</option>
                            <option value="Monthly">Monthly</option>
                            <option value="Quarterly">Quarterly</option>
                            <option value="Annual">Annual</option>
                          </select>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {percentages.category.toFixed(1)}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {percentages.overhead.toFixed(1)}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {percentages.total.toFixed(1)}%
                        </td>
                      </>
                    )}
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => removeExpense(index)}
                        className={`${darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-500 hover:text-red-700'}`}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={`rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-6 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          No expenses added yet. Click "Add" to create your first expense in this category.
        </div>
      )}
    </div>
  );
};

export default OverheadCalculator; 